import superagent from 'superagent'

import Core from 'src/Core'
import Logger from 'src/initializers/Logger'
import airbrake from 'src/initializers/Airbrake'
import { serialize, isURLencodable } from 'src/initializers/Utils'

import { MissingCoreParameterError } from 'src/initializers/Errors'

import { PublicError, UnhandledErrorWithoutStatus } from 'src/Error'
import * as ErrorCodes from 'src/ErrorCodes'

const APP = {
  platform: 'web',
  pek_version: __PEK_VERSION__,
}

// we want to use "Content-Type = application/x-www-form-urlencoded" in order to avoid CORS requests
// but our backend (rails) does not extract array parameters as we want
// for rails :
// "a=1&a=2"       => params === { "a": "2" }
// "a[]=1&a[]=2"   => params === { "a": ["1", "2"] }
// "a[0]=1&a[1]=2" => params === { "a": { "0" => "1", "1" => "2" } }
// the default behavior of superagent expect :
// "a=1&a=2"       => params === { "a": ["1", "2"] }
// so we need to explicitly transform keys "a" into "a[]" for arrays and
// all the items of an array must be of simple type (not an array nor an object)
// if it's not the case, we fallback to JSON but will send a CORS request
superagent.serialize['application/x-www-form-urlencoded'] = serialize

export default class Api {
  constructor ({ clientId, clientSecret, redirectUrl }) {
    this._clientId = clientId
    this._clientSecret = clientSecret
    this._redirectUrl = redirectUrl
    this._resetTokens()
  }

  _resetTokens () {
    this._accessToken = null
    this._refreshToken = null
    this._setNewOAuth2Placeholder()
  }

  _setNewOAuth2Placeholder () {
    this._promiseOAuth2Authorized = new Promise((resolve, reject) => {
      this._promiseOAuth2AuthorizedResolver = resolve
      this._promiseOAuth2AuthorizedRejecter = reject
    })
  }
  _serialize (obj) {
    const str = []
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        str.push(encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]))
      }
    }
    return str.join('&')
  }

  _ajaxOAuth2 (options) {
    if (!this._accessToken) { return Promise.reject(Core.missingUserError()) }
    if (options.data) {
      options.data.access_token = this._accessToken
    } else if (options.formData) {
      options.formData.append('access_token', this._accessToken)
    }
    return this._ajax(options)
  }
  async _ajax ({ retries = 2, ...options }) {
    try {
      return await this._fetch(options)
    } catch (error) {
      if (error.code === ErrorCodes.CONNECTION_FAILED && retries > 0) {
        return this._ajax({ retries: retries - 1, ...options })
      }
      return Promise.reject(error)
    }
  }
  _fetch ({ method, path, url, formData, data, headers, onProgress, unsafeData }) {
    Logger.debug('ajax', arguments)
    const completeUrl = path ? `${__API_BASE_URL__}${path}` : url
    const xhr = superagent[method](completeUrl)

    const httpHeaders = headers || {}
    httpHeaders['Accept'] = 'application/json'

    if (!formData && data && method.toUpperCase() === 'POST' && (!unsafeData || isURLencodable(unsafeData))) {
      httpHeaders['Content-Type'] = 'application/x-www-form-urlencoded'
    }

    for (const key in httpHeaders) {
      if (httpHeaders.hasOwnProperty(key)) {
        xhr.set(key, httpHeaders[key])
      }
    }

    if (onProgress && typeof onProgress === 'function') {
      xhr.on('progress', (e) => {
        if (e.hasOwnProperty('percent')) {
          onProgress(e.percent)
        }
      })
    }

    switch (method.toUpperCase()) {
      case 'GET':
      case 'HEAD':
        xhr.query(data)
        break
      case 'POST':
      case 'PUT':
        xhr.send(formData || data)
    }

    // errors emitted inside `xhr.end` have useless stack trace
    // so we will use the stack trace of this error which is usefull
    const stack = new Error().stack

    const promiseCall = new Promise((resolve, reject) => {
      xhr.end((err, res) => {
        Logger.debug('err', err)
        Logger.debug('res', res)
        // call failed
        if (err || !res.ok) {
          const error = err || res.err
          error.stack = stack
          reject(error)
        // call succeeded
        } else {
          resolve(res)
        }
      })
    })

    // 1. get JSON parsed data
    // 2. some automatic error wrapping
    return promiseCall.then(
      response => response.body,
      error => {
        if (!error.status) {
          if (error.message.startsWith('Request has been terminated')) {
            return Promise.reject(new PublicError(ErrorCodes.CONNECTION_FAILED, error.message))
          }
          airbrake.notify({
            error: new UnhandledErrorWithoutStatus(error),
            params: { module: 'Api', original: error },
          })
        }
        if (error.status >= 500 && error.status < 600) {
          return Promise.reject(new PublicError(ErrorCodes.SERVER_ERROR, error.message))
        }
        if (error.status === 401) {
          return Promise.reject(new PublicError(ErrorCodes.UNAUTHORIZED, error.message))
        }
        if (error.status === 403) {
          return Promise.reject(new PublicError(ErrorCodes.FORBIDDEN, error.message))
        }
        if (
          error.response &&
          error.response.body &&
          error.response.body.error_details
        ) {
          const {
            code,
            debug_message: debugMessage,
            user_message: userMessage,
            invalid_parameter: parameter,
            invalid_reason: reason,
          } = error.response.body.error_details
          const exposedCode = Object.values(ErrorCodes).includes(code) ? code : ErrorCodes.OTHER
          const publicError = new PublicError(exposedCode, debugMessage || userMessage || error.message, { parameter, reason })
          return Promise.reject(publicError)
        }
        return Promise.reject(error)
      },
    )
  }

  disconnect () {
    this._promiseOAuth2AuthorizedRejecter()
    this._promiseOAuth2Authorized.catch(() => null)
    this._resetTokens()
  }
  isAuthorized () {
    return !!(this._accessToken && this._refreshToken)
  }

  async authorizeUserWithIdentifier ({ userExternalId, userEmail: email, customFields }) {
    const data = {
      user: {
        email,
        external_id: userExternalId,
        custom_fields: customFields,
        client_token: this._clientId
      }
    }
    if (Core.get('UAT_MODE')) { data.uat_mode = 't' }

    const responseData = await this._ajax({
      path: '/api/v2/users',
      method: 'post',
      data,
      unsafeData: { customFields },
    })

    const user = responseData['user']
    Core.set('PARTNER_NAME', user.partner_name)
    Core.set('SETTINGS', user.settings)
    Core.setRessource('User', {
      id: user.settings.user_id,
      email: user.email,
      firstname: user.firstname,
      lastname: user.lastname,
    })
    this._accessToken = user['access_token']
    this._refreshToken = user['refresh_token']
    this._promiseOAuth2AuthorizedResolver()
    return Promise.resolve()
  }

  getHome () {
    return this._ajaxOAuth2({ method: 'get', path: '/api/fidmarques/v1/users/home', data: {} })
  }

  createReceiptV2 ({ uuid, eans, capturedAt, receiptSubpicturesCount, customFields, offerIdentifiers }) {
    return this._ajaxOAuth2({
      path: '/api/v2/receipts',
      method: 'post',
      data: {
        uuid,
        captured_at: capturedAt,
        device_uuid: Core.get('DEVICE_UUID'),
        custom_fields: customFields,
        app: APP,
        channel: 'WEB',
        scanned_eans: eans && eans.join(','),
        receipt_subpictures_uuid: uuid,
        receipt_subpictures_count: receiptSubpicturesCount,
        offer_identifiers: offerIdentifiers,
      },
      unsafeData: { customFields },
    })
  }

  uploadSubpicture ({ uuid, capturedAt, receiptId, files, batchUuid, onProgress }) {
    if (!Core.get('SETTINGS')) { return Promise.reject(new MissingCoreParameterError('SETTINGS')) }
    const url = __S3_PROXY_BASE_URL__ + '/v1/sdk/upload'
    const formData = new FormData()
    formData.append('uuid', uuid)
    formData.append('inbox_path', Core.get('SETTINGS').upload_path)
    if (receiptId)  { formData.append('receipt_id',  receiptId) }
    if (batchUuid)  { formData.append('batch_uuid',  batchUuid) }
    if (capturedAt) { formData.append('captured_at', capturedAt) }
    const len = files.length
    for (let i = 0; i < len; i = i + 1) { formData.append('files[]', files[i]) }
    return this._ajax({
      url,
      formData,
      method: 'post',
      onProgress,
    })
  }

  notifySubpictureUploaded ({ receiptId, subpictureUuid }) {
    return this._ajaxOAuth2({
      path: '/api/v2/receipt_subpictures/' + subpictureUuid,
      method: 'put',
      data: {
        receipt_id: receiptId,
        receipt_subpicture_id: subpictureUuid,
      },
    })
  }

  sendBatch ({ batchUuid, receiptUuids, cancelledUuids, createReceipts }) {
    return this._ajaxOAuth2({
      url: __S3_PROXY_BASE_URL__ + '/v1/sdk/send_batch',
      data: {
        batch_uuid: batchUuid,
        receipt_uuids: receiptUuids,
        cancelled_uuids: cancelledUuids,
        create_receipts: createReceipts,
        app: APP,
        device_uuid: Core.get('DEVICE_UUID'),
      },
      method: 'post',
    })
  }

  bulkUpload ({ uuids, files, onProgress }) {
    if (!Core.get('SETTINGS')) { return Promise.reject(new MissingCoreParameterError('SETTINGS')) }
    const url = __S3_PROXY_BASE_URL__ + '/v1/sdk/bulk_upload'
    const formData = new FormData()
    formData.append('inbox_path', Core.get('SETTINGS').upload_path)
    formData.append('device_uuid', Core.get('DEVICE_UUID'))
    formData.append('app', JSON.stringify(APP))
    const len = files.length
    for (let i = 0; i < len; i = i + 1) {
      formData.append('uuids[]', uuids[i])
      formData.append('files[]', files[i])
    }
    const ajaxDetails = {
      url,
      formData,
      method: 'post',
      onProgress,
    }
    return this._ajax(ajaxDetails).then(() => uuids)
  }

  initIframe ({ eans, iframeId, customFields, onSuccess, onFailure, onComplete }) {
    const deviceUuid = Core.get('DEVICE_UUID')
    return this._promiseOAuth2Authorized.then(() => {
      this._callbacksUploadIframe = { onSuccess, onFailure, onComplete }
      const params = {
        h: window.location.origin, // h for host
        a: this._accessToken, // a for access_token
        e: eans, // e for EANs
        d: deviceUuid, // d for device
        s: JSON.stringify(APP), // s for software
        c: 'WEB', // c for channel
        f: JSON.stringify(customFields), // f for fields
      }
      const url = __API_BASE_URL__ + '/web_client/sdk/iframe?' + this._serialize(params)

      const ifrm = document.createElement('iframe')
      ifrm.textContent = 'You need a Frames Capable browser to view this content.'
      ifrm.src = url
      ifrm.style.border = 0
      ifrm.style.width = '100%'
      ifrm.style.height = '100%'

      const ifrmContainer = document.getElementById(iframeId)
      while (ifrmContainer.hasChildNodes()) {
        ifrmContainer.removeChild(ifrmContainer.lastChild)
      }
      ifrmContainer.appendChild(ifrm)
    })
  }

  executeIframeCallback ({ data, onSuccess, onFailure, onComplete }) {
    if ((onSuccess === true) && (typeof this._callbacksUploadIframe.onSuccess === 'function')) {
      data = data.receipt.uuid
      this._callbacksUploadIframe.onSuccess(data)
    }
    if ((onFailure === true) && (typeof this._callbacksUploadIframe.onFailure === 'function')) {
      this._callbacksUploadIframe.onFailure(data)
    }
    if ((onComplete === true) && (typeof this._callbacksUploadIframe.onComplete === 'function')) {
      this._callbacksUploadIframe.onComplete()
    }
  }

  getOffers () {
    return this._ajaxOAuth2({ path: '/api/kitv4/offers', method: 'post', data: {} }).then(data => data.offers)
  }

  getPot () {
    return this._ajaxOAuth2({ path: '/api/kitv4/potv2', method: 'post', data: {} })
  }

  getPotRefunds ({ transferPaymentId }) {
    return this._ajaxOAuth2({
      path: '/api/kitv4/pot_refunds',
      method: 'post',
      data: { transfer_payment_id: transferPaymentId },
    })
  }

  async updatePaymentInformation (params) {
    await this._ajaxOAuth2({
      path: '/api/v2/payment_informations',
      method: 'post',
      data: paymentInformationToApiData(params),
    })
  }

  checkIban ({ iban }) {
    return this._ajaxOAuth2({
      path: '/api/v2/bank_accounts/check_iban',
      method: 'get',
      data: { iban },
    })
  }

  async createTransferPayment () {
    await this._ajaxOAuth2({
      path: '/api/v2/transfer_payments',
      method: 'post',
      data: {},
    })
  }

  async createTransferWithPaymentInformation ({ rememberInformation, ...params }) {
    const data = paymentInformationToApiData(params)
    data.remember_account = rememberInformation
    await this._ajaxOAuth2({
      path: '/api/kitv4/transfer',
      method: 'post',
      data,
    })
  }

  async forgetPaymentInformations () {
    await this._ajaxOAuth2({ path: '/api/kitv4/forget_payment_informations', method: 'post', data: {} })
  }

  findUserWithEmail ({ userEmail }) {
    const data = {
      user_email: userEmail,
      client_id: this._clientId,
      client_secret: this._clientSecret,
    }
    return this._ajax({ path: '/api/fidmarques/v1/sdk/find_user_with_email', method: 'get', data })
  }

  getCardDetails () {
    return this._ajaxOAuth2({ path: '/api/fidmarques/v1/sdk/card', method: 'post', data: {} }).then(data => data.card)
  }

  getRewards () {
    return this._ajaxOAuth2({ path: '/api/fidmarques/v1/sdk/rewards', method: 'post', data: {} })
  }
}

function paymentInformationToApiData ({ firstName, lastName, iban, email }) {
  if (!iban && !email) { return {} }
  let data
  if (iban) {
    data = {
      kind: 'BankAccount',
      bank_account: { firstname: firstName, lastname: lastName, iban }
    }
  } else {
    data = {
      kind: 'PaypalAccount',
      paypal_account: { firstname: firstName, lastname: lastName, email }
    }
  }
  return data
}



// WEBPACK FOOTER //
// ./src/controllers/Api.js